/** Auditor verification UI for a single session's hash chain.
 *
 * Per AGENTS.md §27 / Phase 2A-aud. Read-only deep-dive view for the
 * auditor / org_admin role (gated on the `audit.verify` permission).
 *
 * Loads the full chain (events + tokens) and the verify result, renders:
 *   - StatusBlock — top banner with verify state + counts + chain head.
 *   - TamperDetail — only when the chain is broken.
 *   - ChainTimeline — expandable per-event rows.
 *   - TokenPanel — per-backend anchor cards.
 *   - Download proof — JSON dump of the full chain + tokens for offline
 *     auditor retention.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { PermissionGate } from "@/components/PermissionGate";
import {
  downloadSessionAuditReportPdf,
  downloadSessionAuditSignedReportPdf,
  fetchAllEvents,
  fetchVerify,
} from "@/modules/audit/api";
import { ChainTimeline } from "@/modules/audit/ChainTimeline";
import { StatusBlock } from "@/modules/audit/StatusBlock";
import { TamperDetail } from "@/modules/audit/TamperDetail";
import { TokenPanel } from "@/modules/audit/TokenPanel";
import { VerificationLinksCard } from "@/modules/audit/VerificationLinksCard";


export default function SessionAuditPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  return (
    <AppLayout
      title={t("ui.sessionAudit.title")}
      description={id ? t("ui.sessionAudit.description", { id }) : undefined}
      headerAction={
        <BackLink fallback={`/sessions/${id}`} className="btn-ghost text-sm">
          ← {t("ui.sessionAudit.backToSession")}
        </BackLink>
      }
    >
      <PermissionGate
        perm="audit.verify"
        fallback={
          <p className="card text-sm text-nb-text-2">
            <Trans i18nKey="ui.sessionAudit.permFallback">
              You need the <code>audit.verify</code> permission to view this
              page. Ask an org admin to grant it via the auditor or
              org_admin role.
            </Trans>
          </p>
        }
      >
        {id && <AuditDetail sessionId={id} />}
      </PermissionGate>
    </AppLayout>
  );
}


function AuditDetail({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const verify = useQuery({
    queryKey: ["audit-verify", sessionId],
    queryFn: () => fetchVerify(sessionId),
    refetchInterval: 60_000,
  });
  const events = useQuery({
    queryKey: ["audit-events", sessionId],
    queryFn: () => fetchAllEvents(sessionId),
    // Events are append-only; refresh on verify changes rather than on a
    // separate timer to avoid double-polling.
    enabled: verify.isSuccess,
    refetchInterval: 60_000,
  });
  // Hooks MUST run unconditionally before the early returns below
  // (react-hooks/rules-of-hooks). Keep all useState / useQuery here.
  const [signedPending, setSignedPending] = useState(false);
  const [signedErr, setSignedErr] = useState<string | null>(null);

  if (verify.isLoading) {
    return <p className="text-sm text-nb-text-2">{t("ui.sessionAudit.loadingChain")}</p>;
  }
  if (verify.isError) {
    return (
      <p className="card text-sm text-cvp-danger">
        {t("ui.sessionAudit.loadError", {
          message: (verify.error as Error).message,
        })}
      </p>
    );
  }
  if (!verify.data) return null;
  if (verify.data.reason?.includes("predates")) {
    return (
      <p className="card text-sm text-nb-text-2">
        {t("ui.sessionAudit.predates")}
      </p>
    );
  }

  const onDownload = () => {
    downloadProof({
      sessionId,
      verify: verify.data,
      events: events.data?.events ?? [],
    });
  };

  const onDownloadSigned = async () => {
    setSignedPending(true);
    setSignedErr(null);
    const res = await downloadSessionAuditSignedReportPdf(sessionId);
    if (!res.ok) {
      setSignedErr(
        res.detail
        ?? (res.reason === "kyc_required"
          ? t("ui.sessionAudit.errKycRequired")
          : res.reason === "signing_unavailable"
            ? t("ui.sessionAudit.errSigningUnavailable")
            : t("ui.sessionAudit.errSignedGeneric")),
      );
    }
    setSignedPending(false);
  };

  return (
    <div className="space-y-4">
      <StatusBlock verify={verify.data} />
      {verify.data.broken && <TamperDetail broken={verify.data.broken} />}
      <div className="flex flex-col items-end gap-1">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onDownload}
            disabled={events.isLoading}
          >
            {events.isLoading
              ? t("ui.sessionAudit.loadingEvents")
              : t("ui.sessionAudit.downloadProof")}
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => void downloadSessionAuditReportPdf(sessionId)}
            title={t("ui.sessionAudit.reportTitle")}
          >
            {t("ui.sessionAudit.downloadReport")}
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => void onDownloadSigned()}
            disabled={signedPending}
            title={t("ui.sessionAudit.signedReportTitle")}
            data-testid="audit-download-signed"
          >
            {signedPending
              ? t("ui.sessionAudit.preparing")
              : t("ui.sessionAudit.downloadSignedReport")}
          </button>
        </div>
        {signedErr && (
          <p className="text-xs text-cvp-warn" role="alert">
            {signedErr}
          </p>
        )}
      </div>
      <ChainTimeline
        events={events.data?.events ?? []}
        tamperedSequence={verify.data.broken?.sequence}
        truncated={events.data?.truncated}
      />
      <TokenPanel tokens={verify.data.token_results} />
      <VerificationLinksCard sessionId={sessionId} />
    </div>
  );
}


function downloadProof(args: {
  sessionId: string;
  verify: unknown;
  events: unknown[];
}) {
  const body = {
    session_id: args.sessionId,
    generated_at: new Date().toISOString(),
    verify: args.verify,
    events: args.events,
  };
  const blob = new Blob([JSON.stringify(body, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-${args.sessionId}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
